import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Typography,
  Box,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GroupIcon from '@mui/icons-material/Group'
import { PotentialInviteStatus } from '@swagger'

export interface AwaitingDecisionsTableRow {
  /** LCD who owns these potential invites. When present, the community view is filtered to them. */
  actorKey?: number
  communityKey: number
  communityName: string
  total: number
}

export interface AwaitingDecisionsTableProps {
  rows: AwaitingDecisionsTableRow[]
  /** Academic year selected in the dashboard, kept when navigating to the community. */
  academicYear?: number
}

export const AwaitingDecisionsTable: React.FC<AwaitingDecisionsTableProps> = ({
  rows,
  academicYear,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  /** The dashboard only counts Pending potential invites, so the community list starts filtered to them. */
  const getCommunityAwaitingDecisionsSearch = (
    row: AwaitingDecisionsTableRow
  ) => {
    const searchParams = new URLSearchParams({
      status: PotentialInviteStatus.Pending,
    })

    if (row.actorKey !== undefined) {
      searchParams.set('actorKey', `${row.actorKey}`)
    }

    if (academicYear !== undefined) {
      searchParams.set('academicYear', `${academicYear}`)
    }

    return searchParams.toString()
  }

  if (rows.length === 0) {
    return (
      <Box p={2}>
        <Typography color="textSecondary" align="center">
          {t(
            'TeamLeaderDashboard.AwaitingDecisionsTable.NoResults',
            'No Results Found'
          )}
        </Typography>
      </Box>
    )
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <List disablePadding>
        {rows.map((row, index) => (
          <ListItem
            key={`${row.actorKey}-${row.communityKey}`}
            disablePadding
            divider={index < rows.length - 1}
          >
            <ListItemButton
              onClick={() =>
                navigate({
                  pathname: `/communities/community-details/${row.communityKey}/awaiting-decisions`,
                  search: getCommunityAwaitingDecisionsSearch(row),
                })
              }
              sx={{ py: 2 }}
            >
              <ListItemIcon>
                <GroupIcon color="primary" />
              </ListItemIcon>

              <ListItemText
                primary={row.communityName}
                primaryTypographyProps={{
                  variant: 'subtitle1',
                  fontWeight: 500,
                  color: 'primary.dark',
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={t(
                    'TeamLeaderDashboard.AwaitingDecisionsTable.CountLabel',
                    '{{count}} Awaiting Decisions',
                    { count: row.total }
                  )}
                  color="warning"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                />
                <ChevronRightIcon color="action" />
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  )
}

export default AwaitingDecisionsTable
